// GLOBAL VARIABLE SETUP
// =================================================================================================
// COMPONENTS
import React from 'react';
import SubtitleBlock from './subtitle-block';
import ModalComponent from '../../components/modal';
import EditorModalComponent from './modal';
import EditorConfirmComponent from './confirm';
import FileInput from './file-input';

// HELP FUNCTIONS
import '../../utils/canvas-to-blob';
import '../../utils/kawo-tooltip';

// COMPONENT SPECIFIC IMAGES
import logo from '../../icon.png';
import logo2x from '../../icon@2x.png';

// NPM PACKAGES
import parseSrt from 'parse-srt';
import _ from 'underscore';
import feather from 'feather-icons';

import '../../styles/editor.css';


// MAIN REACT CLASS DECLARATION
// =================================================================================================
class Editor extends React.Component {


	// REACT APP CONSTRUCTOR
	// =============================================================================================
	constructor( props ) {

		super( props );

		this.state = {};

		this.updateLocalstorage = _.debounce( this.saveToLocal, 500 );

		window.trackEvent( 'Pageview', {
			view: 'Editor'
		});
	}


	// =============================================================================================
	componentDidMount() {

		let $this = this;

		window.debugView = this;

		document.querySelector( 'html' ).classList.add( 'scrollfix' );

		this.video = document.querySelector( '#main-video' );

		// DEFAULT TO 0 WHILE IN DEV
		this.video.volume = 0;

		let localProject = window.localProject;

		if ( localProject ) {

			// RESET ALL UNIQUE IDS
			if ( localProject.jsonSrt )
				localProject.jsonSrt.forEach( function( obj, index ) {

					localProject.jsonSrt[ index ].id = _.uniqueId( 'uid_' );
				});

			this.setState( prevState => ({
				jsonSrt: localProject.jsonSrt
			}));

			delete window.localProject;
		}

		this.initialized = false;

		// RE-RENDER ON DURATIONCHANGE
		this.video.addEventListener( 'durationchange', function() {

			if ( !$this.initialized ) {

				$this.videoState( 'pause' );

				let indicator = document.querySelector( '.editor__st-track__indicator' ),
					scrollArea = document.querySelector( '.editor__st-track' );

				scrollArea.style.transform = 'translateX( ' + ( window.innerWidth / 2 ) + 'px )';
				indicator.style.transform = 'translateX( 0px )';

				// IF THEY HAVENT STARTED AFTER A SECOND › START IT
				setTimeout( function() {

					if ( $this.state.paused ) $this.videoState( 'play' );
				}, 1000 );

				$this.initialized = true;
			}

			window.trackEvent( 'Editor', {
				action: 'video-loaded'
			});

			$this.forceUpdate();
		});

		this.video.addEventListener( 'ended', function() {
			this.play();
			$this.resetIndicator();
		});

		window.requestAnimationFrame( $this.setIndicator.bind( $this ) );

		// CAPTURE GLOBAL MOUSE EVENTS AND HANDLE THEM ACCORDING TO ENTRY TARGET
		// TODO: UNBIND THESE ON REMOVE OR SOMETHING
		document.body.addEventListener( 'mouseenter', this.onMouseEnter.bind( this ), true );
		document.body.addEventListener( 'mouseleave', this.onMouseLeave.bind( this ), true );
		document.body.addEventListener( 'mousemove', this.onMouseMove.bind( this ), true );
		document.body.addEventListener( 'mousedown', this.onMouseDown.bind( this ), true );
		document.body.addEventListener( 'mouseup', this.onMouseUp.bind( this ), true );
		document.body.addEventListener( 'click', this.onClick.bind( this ), true );

		window.addEventListener( 'wheel', function( e ) {

			if ( $this.state.looping ) $this.videoState( 'stop-loop' );

			if ( !$this.state.paused ) $this.videoState( 'pause' );

			// DONT ALLOW ON VERY MINUTE SCROLLS SINCE IT LOOKS STUPID AF
			if ( e.deltaX < -2 || e.deltaX > 2 ) {

				$this.pauseIndicator();

				$this.setTimeFromTrack( false, -e.deltaX );

				$this.checkCurrentSubtitleBlock( $this.video.currentTime );
			}

			e.preventDefault();
		});

		document.addEventListener( 'keypress', function( e ) {

			// PLAY/PAUSE ON SPACE
			if ( e.target && !e.target.classList.contains( 'input' ) && e.keyCode === 32 ) {

				e.preventDefault();

				// PLAY IF PAUSED
				if ( $this.state.paused ) $this.videoState( 'play' );

				// IF LOOPING AND SPACE ISNT TRIGGERING A CLICK ON THE STOP LOOP BUTTON ALREADY › STOP LOOP
				else if ( $this.state.looping && ( !e.target || !e.target.dataset || e.target.dataset.action !== 'stop-loop' ) )
					$this.videoState( 'stop-loop' );

				// ELSE › PAUSE
				else $this.videoState( 'pause' );
			}
		});


		// SETUP ELEMENTS TO GET PREVIEW IMAGES
		// -----------------------------------------------------------------------------------------
		this.previewImages = [];

		// SET UP PREVIEW ELEMENTS
		this.previewVideo = document.getElementById( 'previewVideo' );
		this.previewVideo.muted = true;
		this.previewCanvas = document.createElement( 'canvas' );
		this.previewCanvasContext = this.previewCanvas.getContext( '2d' );

		// MAX ALLOWED PLAYBACK RATE
		this.previewVideo.playbackRate = 5;

		window.onbeforeunload = function() {

			$this.saveToLocal();

			return "Are you sure you want to leave? You might lose some changes.";
		}
	}


	// REMOVE EVENT LISTENERS AND SCROLL CLASS
	// =============================================================================================
	componentWillUnmount() {

		document.querySelector( 'html' ).classList.remove( 'scrollfix' );

		document.body.removeEventListener( 'mouseenter', this.onMouseEnter );
		document.body.removeEventListener( 'mouseleave', this.onMouseLeave );
		document.body.removeEventListener( 'mousemove', this.onMouseMove );
		document.body.removeEventListener( 'mousedown', this.onMouseDown );
		document.body.removeEventListener( 'mouseup', this.onMouseUp );
		document.body.removeEventListener( 'click', this.onClick );
	}


	// MAIN RENDER METHOD
	// =============================================================================================
	render() {

		let $this = this,
			jsonSrt = this.state.jsonSrt || [],
			srtError = this.state.srtError,
			activeBlockId = this.state.activeBlockId,
			currentSubtitleBlock = this.state.currentSubtitleBlock,
			videoWidth = ( this.video && this.video.offsetWidth - 40 ),
			errorComponent,
			subtitleBlocks;

		// DEBOUNCED SAVE TO LOCAL
		this.updateLocalstorage();

		if ( srtError ) errorComponent = <ModalComponent error={{ title: 'Oops', msg: srtError }}
			modalHandler={ () => this.setState( prevState => ({ srtError: false }) ) } />;


		// BUILD A LIST OF SUBTITLE BLOCKS
		// -----------------------------------------------------------------------------------------
		if ( jsonSrt && jsonSrt.length ) {
			subtitleBlocks = jsonSrt.map( function( st ) {

				// FIGURE OUT LEFT POS AND WIDTH OF BLOCK
				let blockLeftPos = $this.getPositionFromTime( st.start ) || 0,
					blockWidth = $this.getPositionFromTime( st.end - st.start ) || 0;

				// SET START/END BOUNDARIES BASED ON NEXT/PREVIOUS BLOCKS
				let prevSubtitle, nextSubtitle;

				_.map( jsonSrt, function( subtitleBlock ) {

					// FIND SUBTITLE BLOCK THATS CLOSEST TO THE LEFT OF THE INDICATOR
					if ( subtitleBlock.start < st.start && ( !prevSubtitle || prevSubtitle.start < subtitleBlock.start ) )
						prevSubtitle = subtitleBlock;

					// FIND SUBTITLE BLOCK THATS CLOSEST TO THE RIGHT OF THE INDICATOR
					if ( subtitleBlock.start > st.end && ( !nextSubtitle || nextSubtitle.start > subtitleBlock.start ) )
						nextSubtitle = subtitleBlock;
				});

				return <SubtitleBlock
					key={st.id} editor={$this} ref={st.id}
				  	setActiveBlock={( e ) => $this.setActiveBlock( e )}
				  	data={
						_.extend( st, {
							minStart: prevSubtitle ? prevSubtitle.end : 0,
							maxEnd: nextSubtitle ? nextSubtitle.start : $this.video.duration,
							current: currentSubtitleBlock && st.id && st.id === currentSubtitleBlock.id,
							active: st.id && st.id === activeBlockId,
							leftPos: blockLeftPos,
							width: blockWidth,
							trackWidth: Math.round( $this.video.duration / 10 * window.innerWidth )
						})
				  	}
				/>
			})
		}

		let activeBlock = _.findWhere( jsonSrt, { active: true } ),
			trackWidth = ( this.video ? Math.round( this.video.duration / 10 * window.innerWidth ) : 0 ) + 'px';


		// ADD 10 SECOND BLOCKS TO THE TOP OF THE TRACK AS INDICATORS OF TIME
		// -----------------------------------------------------------------------------------------
		let blocks = 0, excessSeconds, trackBlocks = [];

		if ( this.video ) {

			blocks = Math.floor( this.video.duration / 10 );
			excessSeconds = Math.floor( this.video.duration - ( blocks * 10 ) );

			for ( let i = 0; i < blocks; i++ ) {

				trackBlocks.push(
					<div className="track" key={ 'minute-' + ( i + 1 ) } style={ { width: '100vw', left: i * 100 + 'vw' } }
						 data-start-time={ ( '0' + Math.floor( i / 6 ) ).slice( -2 ) + ':' + ( '0' + (  i % 6 * 10 ) ).slice( -2 ) } />
				);
			}

			if ( excessSeconds > 0 )
				trackBlocks.push(
					<div className="track" key={ 'extra-seconds' } style={ {
						width: Math.round( window.innerWidth * ( excessSeconds / 10 ) ) + 'px',
						left: blocks * 100 + 'vw' } }
						data-start-time={ ( '0' + Math.floor( blocks / 6 ) ).slice( -2 ) + ':' + ( blocks * 10 ) }
						data-end-time={ ( '0' + Math.floor( blocks / 6 ) ).slice( -2 ) + ':' + ( '0' + ( ( blocks * 10 ) + excessSeconds ) ).slice( -2 ) } />
				);
		}


		// -----------------------------------------------------------------------------------------
		return <div className="editor-container">

			{/* ERROR TOP BAR IF ERROR OCCURRED */}
			{ errorComponent ? errorComponent : null }


			{/* ADD HEADER */}
			{/* -------------------------------------------------------------------------------- */}
			<header className="header">
				<div>
					<div onClick={( e ) => this.prevStep( e )} className="logo"><img src={ window.devicePixelRatio >= 2 ? logo2x : logo } /></div>
					<h3>Project: <strong>{ this.props.app.state.projectName }</strong></h3>
				</div>

				{/* BUTTON OPTIONS */}
				<div className="btn-group">

					{/* IF NO JSON SRT › DONT BOTHER SHOWING CONFIRM MODAL */}
					{ !this.state.jsonSrt || !this.state.jsonSrt.length ?

						<div className="btn">
							<input id="fileLoader" type="file" accept="application/x-subrip" onChange={( e ) => this.loadSrt( e ) } />
							Import SRT File
						</div>

					: // ELSE › DISPLAY MODAL FIRST
						<div className="btn" onClick={( e ) => this.importSrt( e )}>Import SRT File</div>
					}



					<button onClick={( e ) => this.confirmDetect( e )} className="btn">Detect Subtitles</button>
					<button onClick={( e ) => this.openModal( e )} className="btn btn_blue">Save</button>
				</div>
			</header>


			{/* MAIN EDITOR SECTION */}
			{/* -------------------------------------------------------------------------------- */}
			<section className="editor">

				<div className="editor__controls" style={{ width: this.video ? this.video.offsetWidth + 'px' : '100%' }}>

					<div>

						<button className="btn btn_trans btn_icon" onClick={ ( e ) => { this.videoState( e ) } }
								data-action={ this.state.paused ? 'play' : 'pause' }>
							{ this.state.paused ?
								<i className="icon" dangerouslySetInnerHTML={ { __html: feather.icons[ 'play' ].toSvg({ width: 22, height: 22, fill: 'currentColor' }) } } />
							:
								<i className="icon" dangerouslySetInnerHTML={ { __html: feather.icons[ 'pause' ].toSvg({ width: 22, height: 22, fill: 'currentColor' }) } } />
							}
						</button>

						<div className={ currentSubtitleBlock ? 'btn btn_trans btn_icon' : 'btn btn_trans btn_icon btn_disabled' } onClick={ ( e ) => { this.videoState( e ) } }
								disabled={ currentSubtitleBlock ? undefined : 'true' }
								data-action={ this.state.looping ? 'stop-loop' : 'start-loop' }>
							{ this.state.looping ?
								<i className="icon" dangerouslySetInnerHTML={ { __html: feather.icons[ 'stop-circle' ].toSvg({ width: 22, height: 22 }) } } />
							:
								<i className="icon" dangerouslySetInnerHTML={ { __html: feather.icons[ 'repeat' ].toSvg({ width: 20, height: 20 }) } } />
							}
						</div>
					</div>

					<div>
						<div className="volume-track volume-track_hidden" data-target="volume">
							<div className="volume-track__inner">
								<div style={ { width: this.video ? this.video.volume * 100 + '%' : '0%' } } />
							</div>
						</div>
						<button className="btn btn_trans btn_icon" data-target="show-hide-volume">
							<i className="icon" dangerouslySetInnerHTML={ { __html: feather.icons[
								( this.video && this.video.volume > 0.5 ? 'volume-2' : ( this.video && this.video.volume > 0 ? 'volume-1' : 'volume-x' ) )
							].toSvg({ width: 22, height: 22 }) } } />
						</button>
					</div>
				</div>

				<div className="editor__video">
					<video id="main-video" src={ this.props.app.state.videoSource } autoPlay="true" />

					<div className="editor__video__subtitles" style={{ maxWidth: videoWidth, fontSize: this.video ? this.video.offsetHeight * 0.05 : 14, bottom: this.video ? Math.floor( this.video.offsetHeight * 0.04 ) + 40 : 60 } }>
						<p dangerouslySetInnerHTML={{__html: this.state.currentSubtitleBlock && this.state.currentSubtitleBlock.text ? this.state.currentSubtitleBlock.text.replace( /\n/g, '<br>' ) : '' }} />
					</div>
				</div>

				<div className="editor__subtitling">
					<div className="editor__st-track-container">

						{/* JUMP TO TIME IN VIDEO TRACK */}
						<div className="editor__jump-track" data-target="jump-track">
							<div className="editor__jump-track__indicator" />
						</div>

						{/* SUBTITLE TRACK */}
						<div className="editor__st-track" style={ { width: trackWidth } } data-target="subtitle-track">

							{trackBlocks}

							{/* TIME INDICATOR */}
							<div className="editor__st-track__indicator" data-target="track-indicator" />

							{/* RENDER SUBTITLE BLOCKS */}
							{subtitleBlocks}
						</div>
					</div>

					<div className="editor__st-form">

						{ this.state.activeBlockId ?
							// SPACER
							<div />

						: null }

						{ this.state.activeBlockId ?

						<button className="btn btn_trans btn_icon fn-delete" onClick={ ( e ) => { this.deleteSubtitle( e ) } }>
							<i className="icon" dangerouslySetInnerHTML={ { __html: feather.icons[ 'trash' ].toSvg({ width: 22, height: 22, stroke: '#626262' }) } } />
						</button>

						: null }

						{ this.state.activeBlockId ?

							<textarea className="input editor__st-input" value={ activeBlock ? activeBlock.text : '' }
									  onInput={( e ) => this.parseContentEditable( e )}
									  onChange={( e ) => this.parseContentEditable( e )}
									  onKeyPress={( e ) => this.parseContentEditable( e )} />
						:
							<button className="btn btn_icon btn_blue" onClick={( e ) => this.createNewSubtitleBlock( e ) }>
								<i className="icon" dangerouslySetInnerHTML={ { __html: feather.icons[ 'plus' ].toSvg({ width: 22, height: 22 }) } } />
							</button>
						}

						{ this.state.activeBlockId ?
							<div className="editor__tip editor__tip_enter"><p>Shift + Enter to save</p></div>
						: null }
					</div>
				</div>
			</section>

			{ this.state.openModal ? <EditorModalComponent Editor={this}/> : null }

			{ this.state.confirmDialogue ?
				<EditorConfirmComponent Editor={this} dialogue={ this.state.confirmDialogue } confirm={ this.state.confirm } />
			: null }


			{/* OFFSCREEN PREVIEW VIEW FOR POPULATING THE JUMP TRACK WITH IMAGES */}
			{/* -------------------------------------------------------------------------------- */}
			<video id="previewVideo" autoPlay muted playsInline
				   src={this.props.app.state.videoSource}
				   onLoadedData={( e ) => this.addPreviewImage( {
					   canvas: this.previewCanvas,
					   context: this.previewCanvasContext,
					   video: this.previewVideo,
					   canvasWidth: this.previewVideo.offsetWidth,
					   canvasHeight: this.previewVideo.offsetHeight
				   } )}/>

			<div className="tooltip"/>
			<div className="scroll-thief"/>

			{/* TODO: WAVELENGTH */}
			{ !this.initialized ? <div className="loading-overlay"><p>Loading...</p></div> : null }
		</div>
	}


	// =============================================================================================
	onMouseEnter( e ) {

		// ON MOUSE ENTER › SHOW THE TOOLTIP IF WE ARENT DRAGGING SOMETHING ELSE
		if ( e.target.dataset && e.target.dataset.target === 'jump-track' ) this.jumpTo( e );

		if ( e.target.dataset && e.target.dataset.target === 'show-hide-volume' ) this.adjustVolume( e );
	}


	// =============================================================================================
	onMouseLeave( e ) {

		// ON MOUSE LEAVE › HIDE THE TOOLTIP
		if ( e.target.dataset && e.target.dataset.target === 'jump-track' ) this.jumpTo( e );

		if ( e.target.dataset && e.target.dataset.target === 'show-hide-volume' ||
			e.target.dataset.target === 'volume' ) this.adjustVolume( e );
	}


	// =============================================================================================
	onMouseDown( e ) {

		if ( !e.target ) return;

		if ( e.target.dataset.target ) {

			this.setState( prevState => ({ dragTarget: e.target.dataset.target } ) );

			switch ( e.target.dataset.target ) {

				case 'volume':
					this.adjustVolume( e );
					break;

				case 'jump-track':
					this.jumpTo( e );
					break;

				case 'subtitle-track':
				case 'track-indicator':
					this.dragIndicator( e );
					break;

				case 'left-handle':
				case 'middle-handle':
				case 'right-handle':

					this.setState( prevState => ({ dragTargetId: e.target.parentNode.dataset.id } ) );

					this.adjustSubtitleBlock( e );

					break;
			}
		}

		// CLEAR ACTIVE BLOCK ON GLOBAL MOUSEDOWN IF WE CLICKED SOMETHING OTHER THAN THE BLOCK OR CONTENTEDITABLE
		if ( ( !e.target.dataset.target || e.target.dataset.target.indexOf( 'handle' ) < 0 ) &&
			!e.target.classList.contains( 'editor__st-input' ) && !e.target.classList.contains( 'fn-delete' ) )
				this.setState( prevState => ({ activeBlockId: null }) );
	}


	// =============================================================================================
	onMouseMove( e ) {

		// WE EITHER NEED A DRAG TARGET OR NEED TO BE MOVING ALONG THE JUMP TRACK
		if ( !this.state.dragTarget && e.target.dataset.target !== 'jump-track' ) return;

		// IF TARGET IS CURRENTLY THE JUMP TRACK › CALL HANDLER TO UPDATE TOOLTIP TIME
		if ( e.target.dataset.target === 'jump-track' ) this.jumpTo( e );

		// CALL HANDLERS BASED ON WHAT WERE DRAGGING
		switch ( this.state.dragTarget ) {

			case 'volume':
				this.adjustVolume( e );
				break;

			// WE STILL NEED TO CALL JUMP TO BASED ON DRAG HERE IN CASE THEY ARE DRAGGING THE TRACK BUT SWITCHED TARGET
			case 'jump-track':
				this.jumpTo( e );
				break;

			case 'subtitle-track':
			case 'track-indicator':
				this.dragIndicator( e );
				break;

			case 'left-handle':
			case 'middle-handle':
			case 'right-handle':
				this.adjustSubtitleBlock( e );
				break;
		}
	}


	// =============================================================================================
	onMouseUp( e ) {

		switch ( this.state.dragTarget ) {

			case 'volume':
				this.adjustVolume( e );
				break;

			case 'jump-track':
				this.jumpTo( e );
				break;

			case 'subtitle-track':
			case 'track-indicator':
				this.dragIndicator( e );
				break;

			case 'left-handle':
			case 'middle-handle':
			case 'right-handle':
				this.adjustSubtitleBlock( e );
				break;
		}

		// REMOVE DRAG TARGET
		this.setState( prevState => ({
			dragTarget: null,
			dragTargetId: null
		} ));
	}


	// =============================================================================================
	onClick( e ) {

		if ( !e.target || !e.target.dataset.target ) return;

		switch ( e.target.dataset.target ) {

			case 'jump-track':
				this.jumpTo( e );
				break;
		}
	}


	// =============================================================================================
	adjustVolume( e ) {

		let track = document.querySelector( '.volume-track' ),
			isVisible = !track.classList.contains( 'volume-track_hidden' );

		// IF THEY CLICKED THE BUTTON › SHOW/HIDE THE VOLUME TRACK
		if ( e.type === 'mouseenter' && ( e.target.dataset.target === 'show-hide-volume' || e.target.dataset.target === 'volume' ) )
			track.classList.remove( 'volume-track_hidden' );

		else if ( e.type === 'mouseup' || ( e.type === 'mouseleave' && !this.state.dragTarget && isVisible && e.toElement !== track ) )
			track.classList.add( 'volume-track_hidden' );

		// ELSE › UPDATE VOLUME ACCORDING TO DRAG
		else if ( e.type === 'mousedown' || ( e.type === 'mousemove' && this.state.dragTarget === 'volume' ) ) {

			this.video.volume = e.pageX < track.offsetLeft ? 0 : ( e.pageX > track.offsetLeft + track.offsetWidth ? 1 :
				( e.pageX - track.offsetLeft ) / 100 );

			this.forceUpdate();
		}
	}


	// =============================================================================================
	adjustSubtitleBlock( e ) {

		if ( this.state.dragTargetId ) {

			// GET REFERENCE OF CURRENT BLOCKS CHILD COMPONENT
			let currentBlock = this.refs && this.refs[ this.state.dragTargetId ],
				handle = this.state.dragTarget.substring( 0, this.state.dragTarget.length - 7 ); // GET HANDLE AFTER REMOVING -handle SUFFIX

			// IF NO PROBLEMS FETCHING ADJUSTBLOCK METHOD ON CHILD › CALL THE METHOD
			if ( typeof currentBlock.adjustBlock === 'function' )
				currentBlock.adjustBlock( e, handle );
		}
	}


	// =============================================================================================
	videoState( e ) {

		let $this = this,
			action = ( typeof e === 'string' ? e : e.target && e.target.dataset.action ),
			currentBlock = this.state.currentSubtitleBlock,
			startTime = currentBlock && currentBlock.start,
			endTime = currentBlock && currentBlock.end;

		if ( typeof e === 'object' && e.currentTarget && e.currentTarget.classList.contains( 'btn_disabled' ) ) return false;

		// PLAY THE VIDEO
		if ( action === 'play' ) {

			this.video.play();

			this.setState( prevState => ({
				paused: false
			}) );

			window.requestAnimationFrame( function() {

				$this.startIndicatorFromCurrentPosition();

				$this.setIndicator();
			});

		// STOP THE VIDEO
		} else if ( action === 'pause' ) {

			this.video.pause();

			this.setState( prevState => ({
				paused: true
			}) );

			this.pauseIndicator();

		// LOOP THE VIDEO
		} else if ( action === 'start-loop' ) {

			this.setState( prevState => ({
				looping: true,
				loopStart: startTime,
				loopEnd: endTime
			}) );

			this.loopVideo();

			this.videoState( 'play' );

		} else if ( action === 'stop-loop' ) {

			this.setState( prevState => ({
				looping: false,
				loopStart: null,
				loopEnd: null
			}) );

			window.requestAnimationFrame( function() {

				$this.startIndicatorFromCurrentPosition();

				$this.setIndicator();
			});
		}
	}


	// =============================================================================================
	loopVideo() {

		let $this = this;

		window.requestAnimationFrame( function() {

			if ( $this.video.currentTime && $this.video.currentTime > $this.state.loopEnd ) {

				if ( !$this.state.looping || !$this.state.currentSubtitleBlock ) return;

				$this.video.currentTime = $this.state.loopStart;

				let indicator = document.querySelector( '.editor__st-track__indicator' ),
					scrollArea = document.querySelector( '.editor__st-track' ),
					jumpIndicator = document.querySelector( '.editor__jump-track__indicator' ),
					currentBlock = document.querySelector( '[data-id="' + $this.state.currentSubtitleBlock.id + '"]' ),
					currentBlockStart = $this.getPositionFromTime( $this.state.loopStart );

				// RESET TRANSITIONS
				indicator.style.transition = 'transform 0s linear, opacity 300ms';
				scrollArea.style.transition = 'transform 0s linear, opacity 300ms';
				jumpIndicator.style.transition = 'width 0s linear, opacity 300ms';

				// SET INDICATOR TO START AND CURRENT BLOCK TO CENTER OF SCREEN
				indicator.style.transform = 'translateX( ' + currentBlockStart + 'px )';
				scrollArea.style.transform = 'translateX( ' +
					-( currentBlockStart - ( window.innerWidth / 2 ) ) + 'px )';

				// UPDATE VIDEO TIME ACCORDING TO POSITION OF INDICATOR
				jumpIndicator.style.width = $this.getPositionFromTime( $this.state.loopStart, $this.video.duration ) + 'px';

				window.requestAnimationFrame( function() {

					if ( !$this.state.looping || !$this.state.currentSubtitleBlock ) return;

					scrollArea.style.transform = 'translateX( -' + ( scrollArea.offsetWidth - ( window.innerWidth / 2 ) ) + 'px )';
					scrollArea.style.transition = 'transform ' + ( $this.video.duration - $this.state.loopStart ) + 's linear, opacity 300ms';

					indicator.style.transform = 'translateX( ' + ( currentBlockStart + currentBlock.offsetWidth ) + 'px )';
					indicator.style.transition = 'transform ' + ( $this.state.loopEnd - $this.state.loopStart ) + 's linear, opacity 300ms';

					jumpIndicator.style.transition = 'width ' + Math.round( $this.video.duration - $this.video.currentTime ) + 's linear';
					jumpIndicator.style.width = window.innerWidth + 'px';
				});
			}

			// IF WE DIDNT STOP LOOPING › CONTINUE LOOP CALL
			if ( $this.state.looping ) $this.loopVideo();
		});
	}


	// =============================================================================================
	getTimeInPosition( position, windowSecondValue ) {

		// IF NOT PASSED A windowSecondValue › EACH WINDOW WIDTH IS WORTH 10 SECONDS
		// NOTE: USED FOR JUMP TRACK RIGHT NOW
		windowSecondValue = windowSecondValue || 10;

		let trackWidth = Math.round( this.video.duration / windowSecondValue * window.innerWidth ),
			duration = this.video.duration;

		return duration / ( 100 / ( position / trackWidth * 100 ) );
	}


	// =============================================================================================
	getPositionFromTime( time, windowSecondValue ) {

		// IF NOT PASSED A windowSecondValue › EACH WINDOW WIDTH IS WORTH 10 SECONDS
		// NOTE: USED FOR JUMP TRACK RIGHT NOW
		windowSecondValue = windowSecondValue || 10;

		let trackWidth = Math.round( this.video.duration / windowSecondValue * window.innerWidth ),
			duration = this.video.duration;

		return trackWidth / duration * time;
	}


	// =============================================================================================
	setIndicator() {

		let indicator = document.querySelector( '.editor__st-track__indicator' ),
			scrollArea = document.querySelector( '.editor__st-track' );

		// IF DRAGGING › SET TRANSFORM TO CURRENT POSITION AND DONT RUN AGAIN
		if ( this.state.dragging || this.state.paused || this.state.looping ) {

			this.initialIndicatorTransformSet = false;
			this.initialScrollAreaTransformSet = false;

			return;
		}

		// IF NO VIDEO › JUST REQUEST ANIMATION FRAME UNTIL IT LOADS
		if ( !this.video || !this.video.duration ) {
			window.requestAnimationFrame( this.setIndicator.bind( this ) );
			return;
		}

		// IF INDICATOR WENT PAST THE CENTER OF SCREEN › SET SCROLL AREA TO START TRANSITIONING BACKWARD
		if ( !this.initialScrollAreaTransformSet ) {

			scrollArea.style.transform = 'translateX( ' + ( window.innerWidth / 2 ) + 'px )';
			indicator.style.transform = 'translateX( 0px )';

			this.startIndicatorFromCurrentPosition();
		}

		this.checkCurrentSubtitleBlock( this.video.currentTime );

		window.requestAnimationFrame( this.setIndicator.bind( this ) );
	}


	// =============================================================================================
	resetIndicator() {

		let video = this.video,
			jumpIndicator = document.querySelector( '.editor__jump-track__indicator' ),
			indicator = document.querySelector( '.editor__st-track__indicator' ),
			scrollArea = document.querySelector( '.editor__st-track' );

		this.initialIndicatorTransformSet = false;
		this.initialScrollAreaTransformSet = false;

		indicator.style.transform = 'translateX( 0px )';
		indicator.style.transition = 'transform 0s linear, opacity 300ms';

		scrollArea.style.transform = 'translateX( -' + ( scrollArea.offsetWidth - ( window.innerWidth / 2 ) ) + 'px )';
		scrollArea.style.transition = 'transform 0s linear, opacity 300ms';

		jumpIndicator.style.width = '0px';
		jumpIndicator.style.transition = 'width 0s linear, opacity 300ms';
	}


	// =============================================================================================
	startIndicatorFromCurrentPosition() {

		// SET INDICATOR FOR SECONDS INDICATOR
		// ------------------------------------------------------------------------------------------
		let indicator = document.querySelector( '.editor__st-track__indicator' ),
			scrollArea = document.querySelector( '.editor__st-track' );

		this.initialScrollAreaTransformSet = true;

		scrollArea.style.transform = 'translateX( -' + ( scrollArea.offsetWidth - ( window.innerWidth / 2 ) ) + 'px )';
		scrollArea.style.transition = 'transform ' + Math.round( this.video.duration - this.video.currentTime ) + 's linear, opacity 300ms';

		indicator.style.transform = 'translateX( ' + ( scrollArea.offsetWidth ) + 'px )';
		indicator.style.transition = 'transform ' + Math.round( this.video.duration - this.video.currentTime ) + 's linear, opacity 300ms';


		// SET INDICATOR FOR JUMP INDICATOR
		// ------------------------------------------------------------------------------------------
		let jumpIndicator = document.querySelector( '.editor__jump-track__indicator' );
		jumpIndicator.style.width = window.innerWidth + 'px';
		jumpIndicator.style.transition = 'width ' + Math.round( this.video.duration - this.video.currentTime ) + 's linear';
	}


	// =============================================================================================
	pauseIndicator() {

		// STOP SECONDS INDICATOR
		// ------------------------------------------------------------------------------------------
		let indicator = document.querySelector( '.editor__st-track__indicator' ),
			scrollArea = document.querySelector( '.editor__st-track' );

		indicator.style.transition = 'transform 0s linear, opacity 300ms';
		scrollArea.style.transition = 'transform 0s linear, opacity 300ms';

		indicator.style.transform = 'translateX( ' + Math.round( indicator.getBoundingClientRect().left - scrollArea.getBoundingClientRect().left ) + 'px )';
		scrollArea.style.transform = 'translateX( ' + Math.round( scrollArea.getBoundingClientRect().left ) + 'px )';


		// STOP JUMP INDICATOR
		// ------------------------------------------------------------------------------------------
		let jumpIndicator = document.querySelector( '.editor__jump-track__indicator' );
		jumpIndicator.style.transition = 'width 0s linear';
		jumpIndicator.style.width = Math.round( jumpIndicator.getBoundingClientRect().width ) + 'px';


		this.initialIndicatorTransformSet = false;
		this.initialScrollAreaTransformSet = false;
	}


	// =============================================================================================
	dragIndicator( e ) {

		let $this = this,
			indicator = document.querySelector( '.editor__st-track__indicator' ),
			scrollArea = document.querySelector( '.editor__st-track' ),
			jumpIndicator = document.querySelector( '.editor__jump-track__indicator' );

		scrollArea.style.transition = 'transform 0s linear, opacity 300ms';
		indicator.style.transition = 'transform 0s linear, opacity 300ms';
		jumpIndicator.style.transition = 'width 0s linear';

		// SET USER IS DRAGGING IF USE MOUSES DOWN
		if ( e.type === 'mousedown' ) {

			this.setState( prevState => ({ dragging: true }) );

			$this.start = e.pageX;

			$this.setTimeFromTrack( e, -( e.pageX - indicator.getBoundingClientRect().left ) );

			this.videoState( 'pause' );

		} else if ( e.type === 'mouseup' ) {

			if ( !this.state.paused ) this.videoState( 'play' );

			this.setState( prevState => ({ dragging: false }) );

		} else if ( this.state.dragging ) {

			window.requestAnimationFrame( function() {

				// PRECAUTION FOR STATE GETTING SET DURING ANIMATION FRAME REQUEST
				if ( !$this.state.dragging ) return;

				$this.end = 0;
				if ( e.pageX ) $this.end = e.pageX;

				if ( $this.prevX && e.pageX < $this.prevX ) {
					if ( $this.direction && $this.direction === 'right' ) $this.start = e.pageX;
					$this.direction = 'left';
				} else {
					if ( $this.direction && $this.direction === 'left' ) $this.start = e.pageX;
					$this.direction = 'right';
				}

				let diff = $this.end - $this.start;

				$this.setTimeFromTrack( e, diff );

				// UPDATE START SO WE DONT ACCUMULATE SPEED
				$this.start = e.pageX;
				$this.prevX = e.pageX;
			});
		}

		this.checkCurrentSubtitleBlock( this.video.currentTime );
	}


	// =============================================================================================
	setTimeFromTrack( e, diff ) {

		let $this = this,
			indicator = document.querySelector( '.editor__st-track__indicator' ),
			scrollArea = document.querySelector( '.editor__st-track' ),
			jumpIndicator = document.querySelector( '.editor__jump-track__indicator' );

		scrollArea.style.transform = 'translateX( ' + Math.round(
				Math.min( window.innerWidth / 2, Math.max( scrollArea.getBoundingClientRect().left + diff, -Math.abs( scrollArea.offsetWidth - window.innerWidth / 2 ) ) )
			) + 'px )';

		indicator.style.transform = 'translateX( ' +
			( scrollArea.getBoundingClientRect().left > 0 ? ( ( window.innerWidth / 2 ) - scrollArea.getBoundingClientRect().left ) :
				Math.round( Math.abs( scrollArea.getBoundingClientRect().left ) + ( window.innerWidth / 2 ) ) ) + 'px )';


		// UPDATE VIDEO TIME ACCORDING TO POSITION OF INDICATOR
		let indicatorTime = this.getTimeInPosition( parseInt( indicator.style.transform.match( /-?[\d.]+px/ )[ 0 ] ) );

		if ( indicatorTime < this.video.duration )
			this.video.currentTime = indicatorTime;

		else this.video.currentTime = this.video.duration - 0.01;

		jumpIndicator.style.width = $this.getPositionFromTime( indicatorTime, $this.video.duration ) + 'px';
	}


	// JUMP TO TIME IN VIDEO
	// =============================================================================================
	jumpTo( e ) {

		let time = this.getTimeInPosition( e.pageX, this.video.duration ),
			jumpIndicator = document.querySelector( '.editor__jump-track__indicator' ),
			indicator = document.querySelector( '.editor__st-track__indicator' ),
			scrollArea = document.querySelector( '.editor__st-track' );

		// IF USER CLICKED › JUMP TO TIME IN VIDEO
		if ( e.type === 'click' || e.type === 'mousedown' || ( e.type === 'mousemove' && this.state.settingPosition ) ) {

			// IF MOUSE DOWN › PAUSE VIDEO AND SET DRAGGING STATE
			if ( e.type === 'mousedown' ) {

				this.setState( prevState => ({ settingPosition: true }) );

				this.start = e.pageX;

				this.videoState( 'pause' );
			}

			// UPDATE VIDEO TIME
			this.video.currentTime = time;

			// UPDATE POSITION OF INDICATORS AND TRACK ACCORDING TO CURRENT TIME
			let position = Math.abs( this.getPositionFromTime( time ) );
			scrollArea.style.transform = 'translateX( ' + Math.round( -position + window.innerWidth / 2 ) + 'px )';
			indicator.style.transform = 'translateX( ' + Math.round( position ) + 'px )';

			jumpIndicator.style.width = e.pageX + 'px';

		// IF MOUSEUP › RELEASE DRAGGING STATE
		} else if ( e.type === 'mouseup' ) this.setState( prevState => ({ settingPosition: false }) );

		if ( e.type !== 'click' ) {

			let tooltip = document.querySelector( '.tooltip' ),
				minutes = Math.floor( time / 60 ),
				seconds = Math.floor( time % 60 );

			if ( e.type === 'mouseenter' ) tooltip.style.visibility = 'visible';

			else if ( e.type === 'mouseleave' ) tooltip.style.visibility = 'hidden';

			if ( e.type === 'mouseenter' || e.type === 'mousemove' ) {

				tooltip.innerHTML = 'Jump to: ' + ( '0' + minutes ).slice( -2 ) + ':' +
					( '0' + seconds ).slice( -2 );

				tooltip.style.top = e.target.getBoundingClientRect().top - 20 + 'px';
				tooltip.style.left = e.pageX - ( tooltip.offsetWidth / 2 ) + 'px';
			}
		}

		this.checkCurrentSubtitleBlock( this.video.currentTime );
	}


	importSrt() {

		this.setState( prevState => ({
			confirmDialogue: 'Are you sure you want to import an SRT? This will overwrite your current subtitle blocks.',
			confirm: <FileInput accept="application/x-subrip" text="Ok" onChange={ ( e ) => this.loadSrt( e ) } />
		}) );
	}


	// IMPORT AN SRT FROM LOCAL DRIVE INTO APP STATE
	// =============================================================================================
	loadSrt( e ) {

		this.setState( prevState => ({
			confirmDialogue: false,
			confirm: false
		}) );

		let $this = this,
			file,
			error,
			reader = new FileReader();

		e.preventDefault();

		// GET VIDEO FILE OBJECT
		file = e.target.files[ 0 ];

		if ( file.type !== 'application/x-subrip' && ( !file.name ||
			file.name.substring( file.name.length - 4, file.name.length ) !== '.srt' ) )
				error = 'You can only import .srt files.';

		if ( !error ) {
			reader.onload = function ( e ) {

				$this.parseSrt( e.target.result );

				$this.setState( prevState => ({ srtFile: file }) );
			};

			reader.readAsText( file );

		} else $this.setState( prevState => ({ srtError: error }) );

		window.trackEvent( 'Editor', {
			action: 'import-srt',
			error: error,
			fileType: file.type,
			projectName: this.props.app.state.projectName
		});
	}


	// CONVERTS JSON AS SRT BACK TO STRING AND EMULATES A CLICK EVENT TO DOWNLOAD THE FILE
	// =============================================================================================
	downloadSrt() {

		if ( !this.state.jsonSrt ) return false;

		let srtAsString = this.parseJSONSrt( this.state.jsonSrt ),
			type = ( /^((?!chrome|android).)*safari/i.test( navigator.userAgent ) ? 'application' : 'text' ),
			a = document.createElement( 'a' );

		document.body.appendChild( a );

		a.style = "display: none";
		a.href = 'data:' + type + '/srt;charset=utf-8,\uFEFF' + srtAsString;
		a.download = this.props.app.state.projectName + '.srt';
		a.click();

		window.trackEvent( 'Editor', {
			action: 'download-srt',
			projectName: this.props.app.state.projectName
		});
	}


	confirmDetect() {

		// IF NO SUBTITLES IN THERE › DO A PSEUDO CLICK SINCE WE DONT WARN THEM OF ANYTHING
		if ( !this.state.jsonSrt || !this.state.jsonSrt.length ) {

			this.detectSrt();

			return;
		}

		this.setState( prevState => ({
			confirmDialogue: 'Detecting subtitles will overwrite any subtitles you\'ve already added. Are you sure you' +
				' want to proceed?',
			confirm: this.detectSrt.bind( this )
		}) );
	}


	// =============================================================================================
	detectSrt() {
		this.setState( prevState => ({ openModal: true, detect: true }))
	}


	// TAKES A STRING AND TURNS IT INTO JSON FORMATTED SRT
	// =============================================================================================
	parseSrt( srtAsString ) {

		let jsonSrt = parseSrt( srtAsString );

		// TODO: DO WE VALIDATE THAT SUBTITLES MEET OUT 1 SEC REQUIREMENT OR JUST LET THEM GO AHEAD?

		// REPLACE ID'S WITH UNDERSCORE ID SYSTEM
		// THIS ALLOWS US TO ADD NEW SUB ELEMENTS WITHOUT HAVING TO FIND THE LATEST ID
		jsonSrt.forEach( function ( st, index ) {

			jsonSrt[ index ].id = _.uniqueId( 'uid_' )
		});

		this.setState( prevState => ({ jsonSrt: jsonSrt }) )
	}


	// CONVERT JSON SRT BACK TO STRING TO PREPARE FOR DOWNLOAD
	// =============================================================================================
	parseJSONSrt( srtAsJSON ) {

		return _.chain( srtAsJSON )
			.sortBy( 'start' )
			.map( function( entry, index ) {

				let secondsToHHMMSS = function( seconds ) {

					let date = new Date(null);

					date.setSeconds( seconds );

					return date.toISOString().substr(11, 8);
				};

				let start = String( entry.start ).split( '.' ),
					end = String( entry.end ).split( '.' ),
					startHHMMSS = secondsToHHMMSS( start[ 0 ] ),
					startMillis = start[ 1 ] || 0,
					endHHMMSS = secondsToHHMMSS( end[ 0 ] ),
					endMillis = end[ 1 ] || 0;

				if ( entry.text )
					return ( index + 1 ) + '\n'
						+ startHHMMSS + ',' + Number( startMillis.toString().substring( 0, 3 ) ) + ' --> '
						+ endHHMMSS + ',' + Number( endMillis.toString().substring( 0, 3 ) ) + '\n'
						+ entry.text.replace( /<br \/>/gi, '\n');
			})
			.value()
			.join( '\n\n' );
	}


	// =============================================================================================
	uploadVideo( type, lang ) {
		let $this = this,
			file = this.props.app.state.videoFile,
			data = new FormData(),
			srtFile;


		// TODO: NOT SURE IT MAKES SENSE HAVING BOTH 'vid' AND 'file' FOR ESSENTIALLY THE SAME THING
		// MERGE
		let xhr = new XMLHttpRequest();


		// SET UP MERGE POST REQUEST PARAMS
		// -----------------------------------------------------------------------------------------
		if ( type === 'merge' ) {
			srtFile = new File(
				[ this.parseJSONSrt( this.state.jsonSrt ) ],
				'somethingSrt.srt',
				{ type: 'application/x-subrip;charset=utf-8' });

			xhr.responseType = 'blob';
			data.append( 'vid', file );
			data.append( 'srt', srtFile );


		// SET UP DETECT POST REQUEST PARAMS
		// -----------------------------------------------------------------------------------------
		} else {
			data.append( 'file', file );
			data.append( 'lang', lang );
		}

		xhr.open( 'POST', 'https://free.subtitle.studio/api/' + type );


		// PROGRESS
		// -----------------------------------------------------------------------------------------
		xhr.upload.addEventListener( 'progress', function( e ) {

			if ( e.lengthComputable ) {
				let progress = Math.floor( ( e.loaded / e.total ).toFixed( 2 ) * 100 );

				$this.setState( prevState => ( progress >= 100

					// REACHED 100, REPLACE PROGRESS WITH PROCESSING
					? { processing: true, progress: false }

					// SET NEW PROGRESS VALUE
					: { progress: progress, processing: false }
				))
			}
		});


		// LOAD
		// -----------------------------------------------------------------------------------------
		xhr.addEventListener( 'load', function( e ) {

			switch ( xhr.status ) {


				// REQUEST OK, START PROCESSING
				// ---------------------------------------------------------------------------------
				case 200:

					// TODO: SERVER IS RETURNING A BLOB WITH LENGTH 0 AT THE MOMENT
					console.log( "ONLOAD LISTENER", type, e.target );

					if ( type === 'merge' ) {

						let blob = new Blob( [ e.target.response ], { type: 'video/mp4' } ),
							a = document.createElement( "a" ),
							url;

						a.style = "display: none";
						document.body.appendChild( a );

						url = window.URL.createObjectURL( blob );

						a.href = url;
						a.download = $this.props.app.state.projectName + '.mp4';
						a.click();

						window.URL.revokeObjectURL( url );

						// RESET MODAL STATE
						$this.setState( prevState => ({
							openModal: false,
							progress: false,
							processing: false
						}) );

					} else {

						$this.parseSrt( e.target.response );

						// RESET MODAL STATE
						$this.setState( prevState => ({
							srtFile: new File( [ e.target.response ], 'somethingSrt', { type: 'application/x-subrip' } ),
							openModal: false,
							detect: false,
							progress: false,
							processing: false
						}) );
					}

					window.trackEvent( 'Upload', {
						action: 'upload-video-success',
						type: type,
						code: 200,
						statusText: xhr.responseType !== 'blob' ? xhr.responseText : 'blob',
						projectName: $this.props.app.state.projectName
					});

					break;


				// API BAD REQUEST
				// ---------------------------------------------------------------------------------
				case 400:

					$this.setState( prevState => ({
						xhrError: 'There was a problem processing your request.',
						progress: false,
						processing: false
					}));

					window.trackEvent( 'Upload', {
						action: 'upload-video-error',
						type: type,
						code: 400,
						statusText: xhr.responseType !== 'blob' ? xhr.responseText : 'blob',
						projectName: $this.props.app.state.projectName
					});

					break;


				// SOMETHING WENT WRONG, LIKELY A CONNECTION ERROR
				// ---------------------------------------------------------------------------------
				default:

					$this.setState( prevState => ({
						xhrError: 'There was a problem connecting to the server.',
						progress: false,
						processing: false
					}));

					window.trackEvent( 'Upload', {
						action: 'upload-video-error',
						type: type,
						code: xhr.status,
						statusText: xhr.responseType !== 'blob' ? xhr.responseText : 'blob',
						projectName: $this.props.app.state.projectName
					});

					break;
			}
		});


		// ERROR
		// -----------------------------------------------------------------------------------------
		xhr.addEventListener( "error", function( e ) {

			let errorMessage = ( e.target && e.target.responseType === 'text' && e.target.responseText )
				|| "Unable to connect to the server.";

			console.log( "XHR Error : " + errorMessage );

			$this.setState( prevState => ({
				xhrError: errorMessage,
				progress: false,
				processing: false
			}) );

			window.trackEvent( 'Upload', {
				action: 'upload-video-error',
				type: type,
				code: e.target && e.target.status,
				statusText: errorMessage,
				projectName: $this.props.app.state.projectName
			});
		});

		xhr.send( data );

		window.trackEvent( 'Upload', {
			action: 'upload-video-initialized',
			type: type,
			lang: lang,
			projectName: $this.props.app.state.projectName
		});
	}


	// =============================================================================================
	parseContentEditable( e ) {

		let $this = this,
			newText = e.target.value,
			activeBlock = _.find( this.state.jsonSrt || [], function( st ) {
				return st.id === $this.state.activeBlockId;
			});

		if ( e.type === 'keypress' && e.charCode === 13 && e.shiftKey ) {

			// SINCE THEY HIT ENTER WHICH IS INTENT TO SAVE › LETS UPDATE LOCALSTORAGE WITHOUT OUR DEBOUNCE
			this.saveToLocal();

			this.setState( prevState => ({ activeBlockId: null }) );

		} else {

			if ( activeBlock ) activeBlock.text = newText;

			this.checkCurrentSubtitleBlock( this.video.currentTime );

			this.forceUpdate();
		}
	}


	// CHECKS WHICH SUBTITLE BLOCK IS CURRENTLY UNDER THE TIME INDICATOR
	// =============================================================================================
	checkCurrentSubtitleBlock( time ) {

		let jsonSrt = this.state.jsonSrt,
			previousBlock = this.state.currentSubtitleBlock;

		// FIND SUBTITLE BLOCK IN CURRENT TIME IN BLOCK TIME RANGE
		let currentBlock = _.find( jsonSrt, function( st ) {
			return time > st.start && time < st.end;
		});

		if ( !currentBlock && !previousBlock ) return;

		// THIS WAS CAUSING SEVERE RENDER THRASHING SO NOW WE ONLY UPDATE IF THERE WAS A CHANGE
		if ( !currentBlock || !_.isEqual( this.state.currentSubtitleBlock, currentBlock ) )
			this.setState( prevState => ({ currentSubtitleBlock: currentBlock }) );
	}


	// SET THE ACTIVE SUBTITLE TRACK BLOCK FOR EDITING
	// =============================================================================================
	setActiveBlock( e ) {

		let activeBlock;

		if ( typeof e === 'string' ) {

			activeBlock = e;

		} else {

			e.stopPropagation();

			activeBlock = _.find( this.state.jsonSrt || [], function( st ) {
				return st.id === e.currentTarget.dataset.id;
			});
		}

		// SET ACTIVE BLOCK TO EDITOR SCENE STATE
		if ( activeBlock ) {

			// CLEAR STATE IF CLICKING ON ALREADY ACTIVE BLOCK
			if ( activeBlock.id === this.state.activeBlockId ) this.setState( prevState => ({ activeBlockId: null }) );

			else {

				// SET ACTIVE SUBTITLE BLOCK ID TO STATE
				this.setState( prevState => ({
					activeBlockId: activeBlock.id
				}) );

				setTimeout( this.focusInput.bind( this ), 16 );
			}
		}
	}


	// =============================================================================================
	createNewSubtitleBlock( e ) {

		this.videoState( 'pause' );

		let $this = this,
			subtitles = this.state.jsonSrt || [],
			indicator = document.querySelector( '.editor__st-track__indicator' ),
			indicatorLeft = parseInt( indicator.style.transform.match( /-?[\d.]+px/ )[ 0 ] ),
			start = Math.max( 0, this.getTimeInPosition( indicatorLeft ) - 1.5 );

		let prevSubtitle, nextSubtitle;

		_.map( subtitles, function( subtitleBlock ) {

			// FIND SUBTITLE BLOCK THATS CLOSEST TO THE LEFT OF THE INDICATOR
			if ( subtitleBlock.start < $this.getTimeInPosition( indicatorLeft ) && ( !prevSubtitle || prevSubtitle.start < subtitleBlock.start ) )
				prevSubtitle = subtitleBlock;

			// FIND SUBTITLE BLOCK THATS CLOSEST TO THE RIGHT OF THE INDICATOR
			if ( subtitleBlock.start > $this.getTimeInPosition( indicatorLeft ) && ( !nextSubtitle || nextSubtitle.start > subtitleBlock.start ) )
				nextSubtitle = subtitleBlock;
		});

		// IF LESS THAN ONE SECOND BETWEEN THE PREVIOUS AND NEXT BLOCK › PREVENT THEM FROM ADDING
		// TODO: DISPLAY ERROR MESSAGE TELLING THEM WHY OR DISABLE BUTTON
		if ( prevSubtitle && nextSubtitle && nextSubtitle.start - prevSubtitle.end < 1.2 ) return;

		start = Math.max( prevSubtitle ? prevSubtitle.end + 0.1 : 0, start );

		// ELSE › ADD A SUBTITLE BETWEEN THE TWO THAT IS A MAXIMUM OF THREE SECONDS OR WILL DROP NICELY IN BETWEEN
		subtitles.push({
			id: _.uniqueId( 'uid_' ),
			start: Number( start.toFixed( 3 ) ),
			end: Number( ( nextSubtitle ? Math.min( start + 3, nextSubtitle.start - 0.1 ) : start + 3 ).toFixed( 3 ) )
		});

		this.setState( prevState => ({
			jsonSrt: subtitles,
			activeBlockId: subtitles[ subtitles.length - 1 ].id
		}) );

		setTimeout( function() {
			$this.checkCurrentSubtitleBlock( $this.video.currentTime );
			$this.focusInput();
		}, 16 );

		window.trackEvent( 'Editor', {
			action: 'create-subtitle'
		});
	}


	// =============================================================================================
	deleteSubtitle( e ) {

		// TODO: ON CHANGE TO LOCALSTORAGE STORE AN UNDO/REDO STATE UP TO 10 REVISIONS

		let $this = this,
			subtitles = this.state.jsonSrt || [],
			activeBlock = _.find( subtitles, function( st ) {
				return st.id === $this.state.activeBlockId;
			});

		if ( !activeBlock ) return;

		subtitles = _.reject( subtitles, function( st ) {
			return st.id === $this.state.activeBlockId;
		});

		this.setState( prevState => ({
			jsonSrt: subtitles,
			activeBlockId: null
		}) );

		setTimeout( function() {
			$this.checkCurrentSubtitleBlock( $this.video.currentTime );
		}, 16 );

		// DONT WAIT FOR RENDER DEBOUNCE TO UPDATE LOCALSTORAGE
		this.saveToLocal();

		window.trackEvent( 'Editor', {
			action: 'delete-subtitle'
		});
	}


	// =============================================================================================
	focusInput() {

		let input = document.querySelector( '.editor__st-input' ),
			range;

		if ( input ) {

			input.focus();

			if ( typeof input.selectionStart === 'number' )
				input.selectionStart = input.selectionEnd = input.value.length;

			else if ( typeof input.createTextRange !== 'undefined' ) {
				input.focus();
				range = input.createTextRange();
				range.collapse( false );
				range.select();
			}
		}
	}


	// =============================================================================================
	saveToLocal() {

		let projectId = this.props.app.state.projectName.replace( /\s/g, '-' ).toLowerCase(),
			projects = JSON.parse( localStorage.getItem( 'projects' ) ) || {};

		// IF WE HAVENT LOADED THE VIDEO YET DONT BOTHER
		if ( !this.video || !this.video.src ) return;

		projects[ projectId ] = {
			projectName: this.props.app.state.projectName,
			jsonSrt: this.state.jsonSrt,
			created: projects[ projectId ] ? projects[ projectId ].created : Date.now(),
			updated: Date.now(),
			videoName: projects[ projectId ] ? projects[ projectId ].videoName : this.props.app.state.videoName // DONT LET VIDEO NAME GET OVERWRITTEN
		};

		localStorage.setItem( 'projects', JSON.stringify( projects ) );
	}


	// =============================================================================================
	addPreviewImage( options ) {

		let jumpTrack = document.querySelector( '[data-target="jump-track"]' );

		if ( !jumpTrack ) return false;

		let $this = this,

			// ASPECT RATIO STUFF BASED ON THE JUMP TRACKS 35px HEIGHT AND THE ASPECT RATIO OF THE VIDEO
			previewImageHeight = 35,
			aspectRatio = previewImageHeight / options.video.videoHeight,
			previewImageWidth = options.video.videoWidth * aspectRatio,

			// MAX NUMBER OF PREVIEW IMAGES THAT WILL FIT ON THE TRACK
			numberOfPreviewImages = ( jumpTrack.offsetWidth / previewImageWidth ).toFixed(),

			// INTERVAL USED TO GET IMAGES AT CORRECT TIME IN VIDEO
			interval = numberOfPreviewImages && options.video.duration ? options.video.duration / numberOfPreviewImages : 0;

		options.canvas.width = previewImageWidth;
		options.canvas.height = previewImageHeight;

		this.drawImageToCanvas( options.canvas, options.context, options.video, options.canvas.width, options.canvas.height );


		// GET BLOB AND ADD TO JUMP TRACK PREVIEW IMAGES
		// -----------------------------------------------------------------------------------------
		options.canvas.toBlob( function ( blob ) {

			// EXIT IF NO DATA
			if ( !blob ) return false;

			// SETUP NEW IMAGE ELEMENT
			let url = URL.createObjectURL( blob ),
				img = document.createElement( 'img' );

			// FREE UP OBJECT URL ONCE IMAGE IS LOADED
			img.onLoad = function () {

				URL.revokeObjectURL( url );
			};

			// SET IMAGE SRC
			img.src = url;

			// ADD IMAGE TO DOM
			jumpTrack.appendChild( img );

			// ADD TO PREVIEW IMAGE ARRAY
			$this.previewImages.push( url );

			// INCREMENT TIME ON PREVIEW VIDEO JUMP TRACK
			options.video.currentTime += interval;

			// SET UP LISTENER IF IT DOESN'T ALREADY EXIST
			if ( !$this.previewVideoListener )
				options.video.addEventListener( 'seeked', function () {

					// PREVENT LISTENERS
					$this.previewVideoListener = true;

					// ADD PREVIEW IMAGE IF WE HAVNT HIT MAXIMUM
					if ( $this.previewImages.length < numberOfPreviewImages ) $this.addPreviewImage( options );

					// REMOVE LISTENER
					else options.video.removeEventListener( 'seeked', this );
				} );
		}, 'image/jpeg', 0.8 );
	}


	// FUNCTION THAT CHECKS FOR BROWSER AND FORMAT EXCEPTIONS AND DRAWS THE IMAGE TO CANVAS
	// =============================================================================================
	drawImageToCanvas( canvas, context, video, width, height ) {

		// TODO: DO WE NEED THIS? - DRAWS CURRENT VIDEO FRAME TO CANVAS AND ACCOUNT FOR IMAGE ORIENTATION
		// if ( this.state.imageNeedsOrientation && this.props.App.state.orientationMatrix && this.props.App.state.orientationMatrix.length ) {
		//
		//
		// 	// EXTRACT ROTATION INFORMATION FROM MATRIX
		// 	// -------------------------------------------------------------------------------------
		// 	let rotation = 0;
		//
		// 	this.props.App.state.orientationMatrix.forEach( function( matrix ) {
		//
		// 		if ( matrix[0][0] && matrix[1][1] ) rotation = 180;
		//
		// 		else if ( matrix[0][1] ) rotation = -90;
		//
		// 		else if ( matrix[1][0] ) rotation = 90;
		// 	});
		//
		//
		// 	// SET ROTATION AND DRAW IMAGE
		// 	// -------------------------------------------------------------------------------------
		// 	switch( rotation ) {
		// 		default :
		// 		case 0:
		// 			canvas.setAttribute( 'width', width );
		// 			canvas.setAttribute( 'height', height );
		// 			context.drawImage( video, 0, 0, width, height );
		// 			break;
		//
		// 		case 90:
		// 			canvas.setAttribute( 'width', width );
		// 			canvas.setAttribute( 'height', height );
		// 			context.rotate( 90 * Math.PI / 180 );
		//
		// 			// DRAW IMAGE TO CANVAS - HEIGTH IS NEGATIVE WIDTH BECAUSE OF TRANSFORM
		// 			if ( navigator.userAgent.indexOf("Firefox") > -1 )
		// 				context.drawImage( video, 0, -width, height, width );
		//
		// 			// FIREFOX AND IOS NEED TO BE HANDLED SLIGHTLY DIFFERENTLY
		// 			else this.drawImageIOSFix( context, video, 0, -width, height, width );
		//
		// 			break;
		//
		// 		case 180:
		// 			canvas.setAttribute( 'width', width );
		// 			canvas.setAttribute( 'height', height );
		// 			context.rotate( 180 * Math.PI / 180 );
		// 			context.drawImage( video, -width, -height, width, height );
		// 			break;
		//
		// 		case 270:
		// 		case -90:
		// 			canvas.setAttribute( 'width', width );
		// 			canvas.setAttribute( 'height', height );
		// 			context.rotate( 270 * Math.PI / 180 );
		//
		// 			// DRAW IMAGE TO CANVAS - HEIGTH IS NEGATIVE WIDTH BECAUSE OF TRANSFORM
		// 			if ( navigator.userAgent.indexOf("Firefox") > -1 )
		// 				context.drawImage( video, -height, 0, height, width );
		//
		// 			// FIREFOX AND IOS NEED TO BE HANDLED SLIGHTLY DIFFERENTLY
		// 			else this.drawImageIOSFix( context, video, -height, 0, height, width );
		// 			break;
		// 	}
		//
		// } else

		context.drawImage( video, 0, 0, width, height );
	}


	// FIX FOR iOS DRAW IMAGE SHAMELESSLY TAKEN FROM STACK OVERFLOW
	// https://stackoverflow.com/questions/11929099/html5-canvas-drawimage-ratio-bug-ios
	// =============================================================================================
	// drawImageIOSFix(ctx, video, dx, dy, dw, dh) {
	//
	// 	// DETECTING VERTICAL SCALING IN iOS IMAGE
	// 	function detectVerticalSquash(video) {
	// 		let ih = video.videoHeight,
	// 			canvas = document.createElement('canvas');
	//
	// 		canvas.width = 1;
	// 		canvas.height = ih;
	//
	// 		let ctx = canvas.getContext('2d');
	//
	// 		ctx.drawImage(video, 0, 0);
	//
	// 		let data = ctx.getImageData(0, 0, 1, ih).data,
	// 			sy = 0,
	// 			ey = ih,
	// 			py = ih;
	//
	// 		while (py > sy) {
	// 			let alpha = data[(py - 1) * 4 + 3];
	//
	// 			if (alpha === 0) ey = py;
	//
	// 			else sy = py;
	//
	// 			py = (ey + sy) >> 1;
	// 		}
	//
	// 		let ratio = (py / ih);
	//
	// 		return (ratio===0)?1:ratio;
	// 	}
	//
	// 	let vertSquashRatio = detectVerticalSquash(video);
	//
	// 	ctx.drawImage(video, dx, dy, dw, dh / vertSquashRatio );
	// }


	// =============================================================================================
	openModal() {
		this.setState( prevState => ({ openModal: true }) )
	}


	// =============================================================================================
	prevStep() {

		return false;

		let projects = JSON.parse( localStorage.getItem( 'projects' ) ) || {},
			projectId = this.props.app.state.projectName.replace( /\s/g, '-' ).toLowerCase(),
			state = {
				view: 'upload'
			};

		if ( projects[ projectId ] )
			window.localProject = projects[ projectId ];

		this.props.app.setState( prevState => ( state ) );
	}
}

export default Editor;


// WEBPACK FOOTER //
// ./src/scenes/Editor/index.js